import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import usePromptParam from '../hooks/usePromptParam';
import { suggestedTopics } from '../data/assistantData';
import { fixMarkdownLineBreaks } from '../utils/markdownUtils';

const STORAGE_KEY = 'assistant_conversations_v1';
const ACTIVE_KEY = 'assistant_active_id_v1';

const AssistantContext = createContext(null);

export function useAssistantContext() {
  const ctx = useContext(AssistantContext);
  if (!ctx) throw new Error('useAssistantContext must be used within AssistantProvider');
  return ctx;
}

// GraphQL query to load user conversations from database
const GET_USER_CONVERSATIONS = gql`
  query GetUserConversations($limit: Int, $includeMessages: Boolean) {
    getUserConversations(limit: $limit, includeMessages: $includeMessages) {
      sessionId
      initialAgentType
      activeAgentType
      createdAt
      updatedAt
      lastActivityAt
      messageCount
      messages {
        id
        role
        content
        timestamp
        suggestedActions
        agentType
      }
    }
  }
`;

// GraphQL mutation to interact with agent
const INTERACT_WITH_AGENT = gql`
  mutation InteractWithAgent(
    $message: String!
    $conversationId: String
    $agentType: String
    $context: JSON
  ) {
    interactWithAgent(
      message: $message
      conversationId: $conversationId
      agentType: $agentType
      context: $context
    ) {
      response
      conversationId
      activeAgentType
      agentSwitched
      switchReason
      availableAgents
      widgetData
      widgetType
      widgetProps
      nextActions
      learningTriggered
      ragSources
      chatHistory {
        id
        role
        content
        timestamp
        suggestedActions
      }
      conversationInfo {
        sessionId
        startedAt
        messageCount
        lastActivity
        initialAgentType
        activeAgentType
        switchCount
      }
    }
  }
`;

function loadPersisted() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const conversations = raw ? JSON.parse(raw) : [];
    // Filter out empty conversations (no messages)
    return conversations.filter((conv) => conv.messages && conv.messages.length > 0);
  } catch (e) {
    console.error('[AssistantContext] Failed to load persisted conversations:', {
      error: e,
      message: e.message,
      storageKey: STORAGE_KEY,
    });
    return [];
  }
}

function savePersisted(convs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(convs));
  } catch (e) {
    console.error('[AssistantContext] Failed to save persisted conversations:', {
      error: e,
      message: e.message,
      storageKey: STORAGE_KEY,
      conversationCount: convs?.length,
    });
  }
}

function saveActive(id) {
  try {
    if (id) localStorage.setItem(ACTIVE_KEY, id);
    else localStorage.removeItem(ACTIVE_KEY);
  } catch (e) {
    console.error('[AssistantContext] Failed to save active conversation ID:', {
      error: e,
      message: e.message,
      activeKey: ACTIVE_KEY,
      conversationId: id,
    });
  }
}

// Transform database conversation to frontend format
function transformDbConversation(dbConv) {
  const messages = (dbConv.messages || []).map((msg) => ({
    id: msg.id,
    role: msg.role === 'agent' ? 'assistant' : msg.role,
    content: msg.content,
    timestamp: msg.timestamp,
    suggestedActions: msg.suggestedActions || [],
    agentType: msg.agentType,
  }));

  const firstUserMessage = messages.find((m) => m.role === 'user');
  const title = firstUserMessage?.content?.slice(0, 50) || 'Conversation';

  return {
    id: dbConv.sessionId,
    title: title + (firstUserMessage?.content?.length > 50 ? '...' : ''),
    preview: firstUserMessage?.content || '',
    timestamp: dbConv.lastActivityAt || dbConv.updatedAt || dbConv.createdAt,
    messages,
    agentType: dbConv.activeAgentType,
  };
}

export function AssistantProvider({ children, initialPrompt }) {
  const paramsFromRouter = useParams();
  const promptFromUrl = usePromptParam();

  const conversationId = paramsFromRouter.conversationId;
  const effectivePrompt = initialPrompt || promptFromUrl;

  const [conversations, setConversations] = useState(() => loadPersisted());
  const [currentMessages, setCurrentMessages] = useState([]);
  const [isThinking, setIsThinking] = useState(false);
  const [hasProcessedPrompt, setHasProcessedPrompt] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [activeAgentType, setActiveAgentType] = useState('succession_dashboard_agent');
  const [hasLoadedFromDb, setHasLoadedFromDb] = useState(false);

  // GraphQL mutation hook
  const [interactWithAgent] = useMutation(INTERACT_WITH_AGENT);

  // GraphQL query to load conversations from database
  const { data: dbConversationsData, loading: loadingConversations } = useQuery(GET_USER_CONVERSATIONS, {
    variables: { limit: 50, includeMessages: true },
    fetchPolicy: 'network-only', // Always fetch fresh from server
    onError: (error) => {
      console.error('[AssistantContext] Failed to load conversations from database:', {
        error,
        message: error.message,
      });
    },
  });

  // Merge database conversations with localStorage on load
  useEffect(() => {
    if (dbConversationsData?.getUserConversations && !hasLoadedFromDb) {
      // Transform and filter out empty conversations (no messages)
      const dbConversations = dbConversationsData.getUserConversations
        .map(transformDbConversation)
        .filter((conv) => conv.messages && conv.messages.length > 0);

      // Merge: use database as source of truth, but keep any localStorage-only conversations
      // that might not have been synced yet
      setConversations((localConvs) => {
        const dbIds = new Set(dbConversations.map((c) => c.id));
        // Also filter out empty local conversations
        const localOnly = localConvs.filter(
          (c) => !dbIds.has(c.id) && c.messages && c.messages.length > 0
        );

        // Database conversations take priority, then any local-only ones
        const merged = [...dbConversations, ...localOnly];

        // Sort by timestamp descending
        merged.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

        console.log('[AssistantContext] Loaded conversations from database:', {
          dbCount: dbConversations.length,
          localOnlyCount: localOnly.length,
          mergedCount: merged.length,
        });

        return merged;
      });

      setHasLoadedFromDb(true);
    }
  }, [dbConversationsData, hasLoadedFromDb]);

  // Load conversation messages when id changes
  useEffect(() => {
    if (conversationId) {
      const conv = conversations.find((c) => c.id === conversationId);
      setCurrentMessages(conv ? conv.messages : []);
      setActiveConversationId(conversationId);
      saveActive(conversationId);
    } else {
      saveActive(null);
    }
  }, [conversationId, conversations]);

  // Persist conversations to storage (only save non-empty conversations)
  useEffect(() => {
    const nonEmptyConversations = conversations.filter(
      (conv) => conv.messages && conv.messages.length > 0
    );
    savePersisted(nonEmptyConversations);
  }, [conversations]);

  // Parse widget data from response
  const parseWidgetData = (response) => {
    const widgetRegex = /\[WIDGET:([^\]]+)\]({[^\[]*})\[\/WIDGET\]/g;
    let match;
    const widgets = [];

    while ((match = widgetRegex.exec(response)) !== null) {
      try {
        const widgetType = match[1];
        const widgetData = JSON.parse(match[2]);
        widgets.push({ type: widgetType, data: widgetData });
      } catch (e) {
        console.error('[AssistantContext] Failed to parse widget data:', {
          error: e,
          widgetType: match[1],
          rawData: match[2],
          conversationId: activeConversationId,
        });
      }
    }

    return widgets;
  };

  // Format message content with widgets
  const formatMessageContent = (response) => {
    // Remove widget tags but keep the text
    const cleaned = response.replace(/\[WIDGET:[^\]]+\]{[^\[]*}\[\/WIDGET\]/g, '');
    // Clean up DCF_ID tags
    const withoutDcfId = cleaned.replace(/DCF_ID:[^\s]+/g, '').trim();
    // Fix markdown line breaks for proper rendering
    return fixMarkdownLineBreaks(withoutDcfId);
  };

  const handleSendMessage = async (content) => {
    if (!content || !content.trim()) return;

    const trimmedContent = content.trim();

    // Handle slash commands
    if (trimmedContent.startsWith('/')) {
      const commandParts = trimmedContent.toLowerCase().split(' ');
      const command = commandParts[0];
      const args = commandParts.slice(1);

      const userMessage = {
        id: `msg-${Date.now()}`,
        role: 'user',
        content: trimmedContent,
        timestamp: new Date().toISOString(),
      };

      let systemMessage = null;

      if (command === '/active_agent') {
        // Show active agent info
        const agentDisplayName = activeAgentType
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

        systemMessage = {
          id: `msg-${Date.now()}-system`,
          role: 'assistant',
          content: `🤖 **Current Active Agent**\n\n**Name:** ${agentDisplayName}\n**Agent ID:** \`${activeAgentType}\`\n**Conversation ID:** \`${activeConversationId || 'No active conversation'}\`\n**Messages:** ${currentMessages.length}`,
          timestamp: new Date().toISOString(),
          isSystem: true,
        };
      } else if (command === '/help') {
        // Show available commands
        systemMessage = {
          id: `msg-${Date.now()}-system`,
          role: 'assistant',
          content: `📋 **Available Slash Commands**\n\n` +
            `**/active_agent** - Show which agent is currently active\n` +
            `**/help** - Show this help message\n` +
            `**/clear** - Clear the current conversation\n` +
            `**/new** - Start a new conversation\n\n` +
            `You can also just chat normally and the system will automatically route your message to the appropriate agent!`,
          timestamp: new Date().toISOString(),
          isSystem: true,
        };
      } else if (command === '/clear') {
        // Clear current conversation
        handleNewConversation();
        systemMessage = {
          id: `msg-${Date.now()}-system`,
          role: 'assistant',
          content: `🗑️ **Conversation Cleared**\n\nStarted a fresh conversation. How can I help you?`,
          timestamp: new Date().toISOString(),
          isSystem: true,
        };
      } else if (command === '/new') {
        // Start new conversation
        handleNewConversation();
        systemMessage = {
          id: `msg-${Date.now()}-system`,
          role: 'assistant',
          content: `✨ **New Conversation Started**\n\nWhat would you like to talk about?`,
          timestamp: new Date().toISOString(),
          isSystem: true,
        };
      } else {
        // Unknown frontend command — pass through to the backend agent (e.g. /debug)
        // Fall out of the slash-command block so the message is sent normally.
        systemMessage = null;
      }

      if (systemMessage) {
        setCurrentMessages((prev) => [...prev, userMessage, systemMessage]);
        return;
      }
      // If no systemMessage, fall through to the regular send path below.
    }

    const userMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: trimmedContent,
      timestamp: new Date().toISOString(),
    };

    setCurrentMessages((prev) => [...prev, userMessage]);
    setIsThinking(true);

    try {
      // Call the real GraphQL API
      // Don't pass agentType - let the backend route based on message content
      const { data } = await interactWithAgent({
        variables: {
          message: content.trim(),
          conversationId: activeConversationId,
          agentType: null, // Always let backend route based on message content
          context: {
            page: 'assistant',
            locale: (typeof window !== 'undefined' && window.locale) || 'en',
          },
        },
      });

      if (data?.interactWithAgent) {
        const result = data.interactWithAgent;

        console.log('[AssistantContext] Raw response:', result.response);
        console.log('[AssistantContext] Widget type:', result.widgetType, 'Widget props:', result.widgetProps);

        // Parse widgets from response text (legacy format)
        let widgets = parseWidgetData(result.response);

        // Also check for widgetType and widgetProps from GraphQL response (new format)
        if (result.widgetType && result.widgetProps) {
          widgets.push({
            type: result.widgetType,
            data: result.widgetProps,
          });
        }
        console.log('[AssistantContext] Final widgets:', widgets);

        const messageContent = formatMessageContent(result.response);
        console.log('[AssistantContext] Message content after widget removal:', messageContent);

        // Create assistant message
        const assistantMessage = {
          id: `msg-${Date.now()}-assistant`,
          role: 'assistant',
          content: messageContent,
          timestamp: new Date().toISOString(),
          widgets: widgets,
          suggestedActions: result.nextActions || [],
          agentType: result.activeAgentType,
          ragSources: result.ragSources || [],
        };

        // Update conversation ID if this is a new conversation
        if (!activeConversationId && result.conversationId) {
          setActiveConversationId(result.conversationId);
        }

        // Update active agent type
        setActiveAgentType(result.activeAgentType);

        setCurrentMessages((prev) => {
          const updated = [...prev, assistantMessage];

          // Update or create conversation
          setConversations((old) => {
            const existingIndex = old.findIndex((c) => c.id === result.conversationId);

            if (existingIndex >= 0) {
              // Update existing conversation
              const newConvs = [...old];
              newConvs[existingIndex] = {
                ...newConvs[existingIndex],
                messages: updated,
                preview: content,
                timestamp: new Date().toISOString(),
                agentType: result.activeAgentType,
              };
              return newConvs;
            } else {
              // Create new conversation
              const newConv = {
                id: result.conversationId,
                title: content.slice(0, 50) + (content.length > 50 ? '...' : ''),
                preview: content,
                timestamp: new Date().toISOString(),
                messages: updated,
                agentType: result.activeAgentType,
              };
              return [newConv, ...old];
            }
          });

          return updated;
        });
      }
    } catch (error) {
      console.error('[AssistantContext] Failed to send message:', {
        error: error,
        message: error.message,
        stack: error.stack,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError,
        conversationId: activeConversationId,
        activeAgentType: activeAgentType,
        userMessage: content.trim(),
        timestamp: new Date().toISOString(),
      });

      // Add error message
      const errorMessage = {
        id: `msg-${Date.now()}-error`,
        role: 'assistant',
        content: (typeof window !== 'undefined' && window.locale) === 'fr'
          ? 'Désolé, une erreur est survenue lors du traitement de votre message. Veuillez réessayer.'
          : 'Sorry, I encountered an error processing your message. Please try again.',
        timestamp: new Date().toISOString(),
        isError: true,
      };

      setCurrentMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsThinking(false);
    }
  };

  const handleNewConversation = () => {
    setCurrentMessages([]);
    setActiveConversationId(null);
    setActiveAgentType('succession_dashboard_agent');
  };

  // Handle initial prompt from URL
  useEffect(() => {
    if (effectivePrompt && !conversationId && !hasProcessedPrompt) {
      console.log('[AssistantContext] Processing prompt:', effectivePrompt);
      setHasProcessedPrompt(true);
      handleSendMessage(effectivePrompt);
    }
  }, [effectivePrompt, conversationId, hasProcessedPrompt]);

  const value = useMemo(
    () => ({
      conversations,
      currentMessages,
      isThinking,
      isLoadingConversations: loadingConversations,
      suggestedTopics,
      handleSendMessage,
      handleNewConversation,
      activeAgentType,
      activeConversationId,
    }),
    [conversations, currentMessages, isThinking, loadingConversations, activeAgentType, activeConversationId]
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

export default AssistantContext;
